import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { EmptyState } from '@rossum/components/empty-state/EmptyState';
import { boldText } from '@rossum/components/variable-suggestions/boldText';
import { IconChevronRight, IconCircleX } from '@rossum/ui/icons/tabler';
import {
  ListItemButton,
  Paper,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';

type AssignedQueuesListProps = {
  queueUrls: string[];
  onRemove: (queueUrl: string) => void;
  isReadOnly: boolean;
};

const cancelIconClassName = 'remove-assigned-queue-icon';

const LoadingSkeletons = () => (
  <Stack spacing={1} useFlexGap>
    {[1, 2, 3].map(no => (
      <Stack key={no} direction="row" spacing={1} useFlexGap>
        <Skeleton variant="text" width={100} height={25} />
        <Skeleton variant="text" width={100} height={25} />
      </Stack>
    ))}
  </Stack>
);

export const AssignedQueuesList = ({
  queueUrls,
  onRemove,
  isReadOnly,
}: AssignedQueuesListProps) => {
  const intl = useIntl();
  const { workspacesWithQueues: queuesByWorkspace, isLoading } =
    useWorkspacesWithQueues({
      enableQueries: true,
    });

  const queuesByWorkspaceMap = useMemo(
    () =>
      R.pipe(
        queuesByWorkspace ?? [],
        R.flatMap(workspace =>
          workspace.queues.map(q => ({ ...q, workspaceName: workspace.name }))
        ),
        R.mapToObj(queue => [queue.url, queue])
      ),
    [queuesByWorkspace]
  );

  const listItems = useMemo(
    () =>
      R.pipe(
        queueUrls,
        R.map(url => queuesByWorkspaceMap[url]),
        R.filter(R.isNonNullish)
      ),
    [queueUrls, queuesByWorkspaceMap]
  );

  return (
    <Stack spacing={0.5} useFlexGap sx={{ maxHeight: 250, overflowY: 'auto' }}>
      {isLoading ? (
        <LoadingSkeletons />
      ) : listItems.length === 0 ? (
        <EmptyState
          title={intl.formatMessage({
            id: 'containers.savedViews.drawer.emptyQueues.title',
          })}
          description={intl.formatMessage(
            {
              id: 'containers.savedViews.drawer.emptyQueues.description',
            },
            { boldText }
          )}
        />
      ) : (
        listItems.map(({ id, workspaceName, name, url }) => (
          <ListItemButton
            component={Paper}
            elevation={0}
            onClick={() => onRemove(url)}
            disabled={isReadOnly}
            key={id}
            sx={{
              pr: 1,
              pl: 2,
              py: 0.5,
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRadius: borderRadius('m'),
              '&:hover': {
                [`& .${cancelIconClassName}`]: { visibility: 'visible' },
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              divider={
                <SvgIcon fontSize="small" color="disabled">
                  <IconChevronRight />
                </SvgIcon>
              }
              sx={{ alignItems: 'center', overflow: 'hidden' }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {workspaceName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </Typography>
            </Stack>
            <SvgIcon
              className={cancelIconClassName}
              sx={{ visibility: 'hidden' }}
            >
              <IconCircleX />
            </SvgIcon>
          </ListItemButton>
        ))
      )}
    </Stack>
  );
};
